import { useState, useMemo } from 'react';

interface SortConfig {
  key: string | null;
  direction: 'ascending' | 'descending';
}

// Define status priority for sorting
const STATUS_PRIORITY = {
  active: 1, // พร้อมใช้งาน
  in_servicing: 2, // กำลังล้าง
  repairing: 3, // กำลังซ่อม
  eol: 4, // สิ้นสุดการใช้งาน
};

type StatusType = keyof typeof STATUS_PRIORITY;

export const useSort = <T extends Record<string, any>>(data: T[]) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'ascending',
  });

  const getSortValue = (item: T, key: string) => {
    // Handle nested object paths (e.g., 'customer.name')
    const keys = key.split('.');
    let value = item;
    for (const k of keys) {
      value = value?.[k];
    }

    // Return the appropriate value based on the column
    switch (key) {
      case 'status':
      case 'active':
        // Get the status priority value for sorting
        const status = String(value || '').toLowerCase() as StatusType;
        return {
          priority: STATUS_PRIORITY[status] || 999, // Default to high number for unknown status
          label: value, // Keep original value for display
        };
      case 'shotCount':
        return Number(value) || 0;
      case 'moldNumber':
        return value || '';
      case 'id':
      case 'productParts':
        // For arrays, join them into a string for comparison
        return Array.isArray(value)
          ? value.map((part) => part.partNumber).join(',')
          : String(value || '');
      case 'customer':
        // For customer object, sort by customer code
        return value?.code || '';
      default:
        return String(value || '');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key!);
      const bValue = getSortValue(b, sortConfig.key!);

      // Handle status sorting
      if (sortConfig.key === 'status' || sortConfig.key === 'active') {
        const comparison = aValue.priority - bValue.priority;
        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      }

      // Handle number sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue;
      }

      // For strings, use localeCompare with Thai language support
      return sortConfig.direction === 'ascending'
        ? String(aValue).localeCompare(String(bValue), 'th')
        : String(bValue).localeCompare(String(aValue), 'th');
    });
  }, [data, sortConfig]);

  const requestSort = (key: string) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === 'ascending'
          ? 'descending'
          : 'ascending',
    }));
  };

  return { sortedData, sortConfig, requestSort };
};
